import React, { useEffect, useContext, useState, useRef, useCallback } from "react";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet";
import { confirmAlert } from "react-confirm-alert";
import { MdDelete } from "react-icons/md";
import { useTranslation } from "react-i18next";
import axios from "axios";

// Contexts
import { AuditContext } from "../../../../contexts/Audit";
import { UserContext } from "../../../../contexts/User";

// Functions & Components
import { BASE_URL } from "../../../../BaseUrl";
import { _t, getCookie } from "../../../../functions/Functions";
import DataTable from "../../../common/DataTable";
import AuditSidebar from "./AuditSidebar";
import { ToastSuccess, ToastError } from "../../../../functions/ToastShow";
import DebouncedInput from "../../../../components/DebouncedInput";

const RcrQuestionList = () => {
  const { t } = useTranslation();
  const { authUserInfo } = useContext(UserContext);

  const {
    getPaginatedRcrQuestions,
    rcrQuestionList,
    rcrQuestionListForSearch,
    setRcrQuestionList,
    setRcrQuestionListForSearch,
    rcrSections,
    getRcrSections,
    loading,
    dataPaginating,
    setDataPaginating,
  } = useContext(AuditContext);

  const [searchedQuestions, setSearchedQuestions] = useState({
    list: null,
    searched: false,
  });
  
  const [filterSection, setFilterSection] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!rcrSections) getRcrSections();
  }, []);
  
  useEffect(() => {
    console.log("abcd8",searchTerm,filterSection)
    fetchQuestions(1, searchTerm);
    setSearchedQuestions(prev => ({ ...prev, searched: searchTerm.length > 0 }));
  }, [filterSection, searchTerm]);

  const fetchQuestions = (page, searchText = "") => {
    const payload = { page };
    if (filterSection) payload.section_id = filterSection;
    if (searchText) payload.search = searchText;
    
    getPaginatedRcrQuestions(payload);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleFetchData = (page) => {
    fetchQuestions(page, searchTerm);
  };

  const getListData = useCallback(() => {
    return rcrQuestionList?.data || [];
  }, [rcrQuestionList]);

  const onClearSearch = () => {
    setSearchTerm("");
  };

  const handleDeleteConfirmation = (id) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body">
            <h1>{_t(t("Are you sure?"))}</h1>
            <p className="text-center">{_t(t("You want to delete this question?"))}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary"
                onClick={() => {
                  handleDeleteQuestion(id);
                  onClose();
                }}
              >
                {_t(t("Yes, delete it!"))}
              </button>
              <button className="btn btn-success ml-2 px-3" onClick={onClose}>
                {_t(t("No"))}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const handleDeleteQuestion = async (id) => {
    setDataPaginating(true);
    const url = BASE_URL + `/settings/admin-questions/${id}`;
    try {
      const res = await axios.delete(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      // Assuming backend returns updated lists or we re-fetch
      fetchQuestions(1, searchTerm);
      setDataPaginating(false);
      ToastSuccess({ title: `${_t(t("Question has been deleted"))}` });
    } catch (error) {
      setDataPaginating(false);
      ToastError({ title: `${_t(t("Please try again"))}` });
    }
  };

  const COLUMNS = [
    { 
      label: "S/L", 
      render: (item, i) => searchedQuestions.searched
        ? i + 1
        : i + 1 + ((rcrQuestionList?.current_page || 1) - 1) * (rcrQuestionList?.per_page || 0)
    },
    { label: "Section", render: (item) => <>{item.section?.name || item.section_name || item.section?.title}</> },
    { label: "Question", render: (item) => <div className="text-wrap" style={{maxWidth: '300px'}}>{item.question_text || item.title || item.question}</div> },
    { label: "Order", render: (item) => <>{item.sort_order || item.s_no}</> },
    { label: "Max Marks", render: (item) => <div className="font-weight-bold text-center">{item.max_marks || item.weight}</div> },
    { label: "Critical", render: (item) => (
        item.is_critical ? <span className="badge badge-danger sm-text">Yes</span> : <span className="badge badge-secondary sm-text">No</span>
      ) 
    },
    { label: "Active", render: (item) => (
        item.is_active ? <span className="badge badge-primary sm-text">Active</span> : <span className="badge badge-warning sm-text">Inactive</span>
      ) 
    },
    {
      label: "Action", render: (item) => (
        <div className="d-flex justify-content-center">
          <NavLink
            title="Edit"
            className="btn btn-primary ml-2 rounded-sm"
            to={`/dashboard/audit/rcr-question-edit/${item.id}`}
            style={{ height: "1.7rem", width: "1.7rem" }}
          >
            <div className="d-flex align-items-center justify-content-center">
              <i className="fa fa-pencil edit-icon" aria-hidden="true"></i>
            </div>
          </NavLink>
          <button
            title="Delete"
            className="btn btn-danger ml-2 rounded-sm del-btn"
            onClick={() => handleDeleteConfirmation(item.id)}
          >
            <MdDelete className="del-icon" />
          </button>
        </div>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>{_t(t("Audit Questions"))}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <AuditSidebar />
            </div>

            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="row mx-3 gx-2 align-items-center t-pt-15 t-pb-15">
                  <div className="col-md-5 col-lg-4 t-mb-15 mb-md-0">
                    <ul className="t-list fk-breadcrumb">
                      <li className="fk-breadcrumb__list">
                        <span className="t-link fk-breadcrumb__link text-capitalize">
                          {!searchedQuestions.searched
                            ? _t(t("Audit Question List"))
                            : _t(t("Search Result"))}
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="col-md-7 col-lg-8">
                    <div className="row gx-3 align-items-center">
                      <div className="col-md-4 t-mb-15 mb-md-0">
                        <select
                          className="form-control border-secondary-light"
                          value={filterSection}
                          onChange={(e) => setFilterSection(e.target.value)}
                        >
                          <option value="">{_t(t("All Sections"))}</option>
                          {rcrSections && rcrSections.map((sec, idx) => (
                            <option key={idx} value={sec.id}>
                              {sec.name || sec.title || `Section ${sec.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-5 t-mb-15 mb-md-0">
                        <div className="input-group">
                          <div className="form-file">
                            <DebouncedInput
                              value={searchTerm}
                              type="text"
                              className="form-control border-0 form-control--light-1 rounded-left"
                              placeholder={_t(t("Search question text..."))}
                              onChange={handleSearch}
                              disabled={loading || dataPaginating}
                              debounce={1500}
                            />
                          </div>
                          <button className="btn btn-primary rounded-right" type="button">
                            <i className="fa fa-search" aria-hidden="true"></i>
                          </button>
                        </div>
                      </div>
                      <div className="col-md-3 text-md-right">
                        <NavLink
                          to="/dashboard/audit/rcr-question-add"
                          className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                        >
                          {_t(t("add new"))}
                        </NavLink>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <DataTable
                loading={loading || dataPaginating}
                data={getListData()}
                columns={COLUMNS}
                emptyText="No questions available"
                pagination={{
                  isPageLoading: loading,
                  isPagination: true,
                  pageCount: rcrQuestionList?.last_page,
                  currentPage: rcrQuestionList?.current_page,
                  from: rcrQuestionList?.from,
                  to: rcrQuestionList?.to,
                  total: rcrQuestionList?.total,
                }}
                handleFetchData={handleFetchData}
                searchPayload={{
                  isSearched: searchedQuestions.searched,
                  onClearSearch
                }}
              />
            </div>
          </div>
        </div>
      </main>
      
      <style>{`
        .border-secondary-light { border-color: #e2e8f0; border-radius: 0.25rem; font-size: 13px; height: calc(1.8rem + 2px); padding: 0.25rem 0.5rem; }
      `}</style>
    </>
  );
};

export default RcrQuestionList;
